import { ProductSuggestionsWrapper } from "../SearchResults.styled";
import { ResultHeader } from "./ResultHeader";
import { Trans } from "@lingui/macro";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import { useCallback, useContext, useMemo } from "react";
import type { ProductSuggestion } from "src/types/adapters/query.type";
import {
  formatResultsTotal,
  productSuggestionToCatalogProduct,
} from "../utils";
import { SearchResultsDispatchContext } from "../search-results.context";
import { ProductCardWishlist } from "src/organisms/ProductCardWishlist";
import { Grid } from "src/organisms/Grid";
import { Button } from "src/atoms/Button";
import Link from "next/link";
import { theme } from "src/theme";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { locale } from "src/config/app-config";

type ProductSuggestionListProps = {
  products: ProductSuggestion[];
  total: number;
  query: string;
};

function ProductSuggestionList({
  products,
  total: totalResults,
  query,
}: ProductSuggestionListProps) {
  // Must stay named `total`: it is the Lingui placeholder in "Show {total} results".
  const total = formatResultsTotal(totalResults);
  const { currentShopType } = useShopTypeContext();
  const { onSuggestionClick, onViewAllClick } = useContext(
    SearchResultsDispatchContext
  );

  const viewAllLink = `/search${
    currentShopType !== ShopType.All
      ? getI18nRoute({ route: `/${currentShopType}`, locale })
      : ""
  }?q=${encodeURIComponent(query)}&q-type=page`;

  const handleViewAllHeaderClick = useCallback(
    () => onViewAllClick("header"),
    [onViewAllClick]
  );

  const handleViewAllFooterClick = useCallback(
    () => onViewAllClick("bottom"),
    [onViewAllClick]
  );

  const productItems = useMemo(() => {
    return products.map((product, idx) => (
      <ProductCardWishlist
        key={product.id}
        product={productSuggestionToCatalogProduct(product)}
        position={idx}
        zoomImageOnHover={true}
        showHoverBorder={true}
        showName={false}
        cnstrcSection="Products"
        onSearchProductClick={() =>
          onSuggestionClick({
            gateLevel: product.gateLevel,
            itemId: product.id,
            position: idx,
            type: product.type,
          })
        }
      />
    ));
  }, [products, onSuggestionClick]);

  const viewAllButton = useMemo(
    () =>
      totalResults > 6 ? (
        <Link href={viewAllLink} prefetch={false}>
          <Button
            fullWidth
            variant="secondary"
            style={{ marginBlockStart: theme.space.space24 }}
            onClick={handleViewAllFooterClick}
            data-cnstrc-search-submit-btn
          >
            <Trans>Show {total} results</Trans>
          </Button>
        </Link>
      ) : null,
    [total, totalResults, viewAllLink, handleViewAllFooterClick]
  );

  return (
    <ProductSuggestionsWrapper>
      <ResultHeader
        title={<Trans>Products</Trans>}
        viewAllText={<Trans>Show {total} results</Trans>}
        viewAllLink={totalResults > 6 ? viewAllLink : undefined}
        onViewAllClick={totalResults > 6 ? handleViewAllHeaderClick : undefined}
        isSearchSubmit
      />

      <Grid
        items={productItems}
        footerElement={viewAllButton}
        itemsPerColumn={{ desktop: 6, tablet: 3, mobile: 2 }}
        gap={{ desktop: "space24", tablet: "space16", mobile: "space16" }}
        hideFooterOn={{ desktop: true, tablet: true }}
      />
    </ProductSuggestionsWrapper>
  );
}

export { ProductSuggestionList };
